import React, { useEffect, useState } from 'react';
import { useParams, useLocation, useNavigate } from 'react-router-dom';
import Footer from '../components/Footer';
import LoadingPage from '../components/LoadingPage';
import { landingPageService } from '../services/landingPageService';
import { CreatorLandingPage } from '../types/database';

const Success: React.FC = () => {
  const { creatorId } = useParams<{ creatorId: string }>();
  const location = useLocation();
  const navigate = useNavigate();
  const [landingPageData, setLandingPageData] = useState<CreatorLandingPage | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  // Get email from navigation state
  const email = location.state?.email;
  const fromPath = location.state?.from || '/';

  useEffect(() => {
    const fetchData = async () => {
      try {
        const landingPage = await landingPageService.getLandingPageByCreatorId(creatorId || '');
        
        if (!landingPage) {
          throw new Error('Landing page not found');
        }

        setLandingPageData(landingPage);

        // Set the accent color if we have landing page data
        if (landingPage.accent_color) {
          const accentColor = landingPage.accent_color.startsWith('#') 
            ? landingPage.accent_color 
            : `#${landingPage.accent_color}`;
          document.documentElement.style.setProperty('--accent-color', accentColor);
        }

      } catch (err) {
        console.error('Error fetching landing page data:', err);
        setError('Failed to load page data');
      } finally {
        setIsLoading(false);
      }
    };

    if (creatorId) {
      fetchData();
    }
  }, [creatorId]);

  if (isLoading) {
    return <LoadingPage message="Loading your success page..." />;
  }

  if (error) {
    return (
      <div style={{ 
        minHeight: '100vh', 
        display: 'flex', 
        alignItems: 'center', 
        justifyContent: 'center',
        fontFamily: 'Gabarito, sans-serif',
        color: 'red'
      }}>
        {error}
      </div>
    );
  }

  return (
    <div className='success-container'>
      <div className='success-header'>
        <div className='success-header-content'>
          <span className='success-title paragraphs medium color charcoal'>Welcome to {landingPageData?.name}</span>
          <div className='success-logo'>
            {landingPageData?.logo && (
              <img 
                src={landingPageData.logo} 
                alt={landingPageData.name} 
                style={{ maxHeight: '40px', width: 'auto' }}
              />
            )}
          </div>
          <button className='success-back paragraphs medium color charcoal' onClick={() => navigate(fromPath)}>Back to Home</button>
        </div>
      </div>
      
      <div className='success-content'>
        <div className='success-card'>
          <div className='success-icon'>
            <svg width="64" height="64" viewBox="0 0 64 64" fill="none" xmlns="http://www.w3.org/2000/svg">
              <circle cx="32" cy="32" r="32" fill="var(--accent-color)"/>
              <path d="M20 32L28 40L44 24" stroke="white" strokeWidth="3" strokeLinecap="round" strokeLinejoin="round"/>
            </svg>
          </div>
          
          <h1 className='titles large color black'>Account Created Successfully!</h1>
          
          <p className='paragraphs medium color charcoal'>
            Thank you for joining {landingPageData?.name}. We've sent a verification email to:
          </p>
          
          <div className='email-display'>
            <span className='paragraphs medium color black'>{email}</span>
          </div>
          
          <div className='verification-steps'>
            <h2 className='subtitles medium color black'>Next Steps:</h2>
            <ol className='steps-list'>
              <li className='paragraphs medium color charcoal'>
                Check your email inbox (and spam folder)
              </li>
              <li className='paragraphs medium color charcoal'>
                Click the verification link in the email
              </li>
              <li className='paragraphs medium color charcoal'>
                Once verified, you'll have full access to your account
              </li>
            </ol>
          </div>
          
          <div className='success-actions'>
            <button 
              className='btn primary'
              onClick={() => navigate(fromPath)}
            >
              Return to {landingPageData?.name || 'Home'}
            </button>
          </div>
          
          <div className='help-text'>
            <p className='paragraphs small color charcoal'>
              Didn't receive the email? Check your spam folder or contact support.
            </p>
          </div>
        </div>
      </div>
      
      <Footer creatorId={creatorId} />
    </div>
  );
};

export default Success; 